/* eslint-disable ember/no-computed-properties-in-native-classes */
/* eslint-disable ember/no-runloop */
import { computed } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import getChartColor from 'frontend-utils/utils/hotel-chart-color';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';

interface HotelStrategyChartsChartRoomtypePatternDetailSignature {
  Args: {
    containerHeight: unknown;
    selectedRoomtypeData: unknown;
  };
}

export default class HotelStrategyChartsChartRoomtypePatternDetail extends Component<HotelStrategyChartsChartRoomtypePatternDetailSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare rsSelections: Services['rs-selections'];

  @tracked hoveredCompetitor = '';

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartCategories() {
    // This will determine the categories of the chart (every category is a hotel/competitor)
    const competitors = this.rsSelections.filteredCompetitors.map(
      (competitor) => ({
        id: competitor.id,
        name: competitor.name,
      }),
    );

    // Always place own hotel first
    competitors.unshift({
      // @ts-expect-error: Incorrect type
      id: this.hotelAndMarketService.hotel.hotelinfo.id,
      // @ts-expect-error: Incorrect type
      name: this.hotelAndMarketService.hotel.hotelinfo.name,
    });

    return competitors;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // @ts-expect-error: Incorrect type
    const series = [];
    const numberOfHotels = this.rsSelections.filteredCompetitors.length + 1;

    if (!this.sortedSelectedRoomtypePatterns) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return series;
    }

    // Goal of this chart: Show for every competitor and every room type all the room names under that room type
    // Every roomname will be a series in this chart
    this.chartCategories.forEach((hotel, categoriesIndex) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const hotelPatternData =
        this.sortedSelectedRoomtypePatterns[categoriesIndex];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (hotelPatternData && !hotelPatternData.hasNoData) {
        const color = getChartColor(
          // @ts-expect-error: Incorrect type
          hotel.id,
          this.hotelAndMarketService.primaryAndSecondaryCompetitors,
        );

        const patternSeriesConfig =
          HotelStrategyChartsChartRoomtypePatternDetail.generateSeriesConfig(
            hotelPatternData,
            color,
          );

        series.push(
          HotelStrategyChartsChartRoomtypePatternDetail.fillUpSeriesDataWithDummyValues(
            patternSeriesConfig,
            hotelPatternData,
            numberOfHotels,
            categoriesIndex,
          ),
        );

        // Now also loop over the subpatterns of the current pattern
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        hotelPatternData.rssubpatterns.forEach((subpattern) => {
          const subpatternSeriesConfig =
            HotelStrategyChartsChartRoomtypePatternDetail.generateSeriesConfig(
              subpattern,
              color,
            );

          series.push(
            HotelStrategyChartsChartRoomtypePatternDetail.fillUpSeriesDataWithDummyValues(
              subpatternSeriesConfig,
              subpattern,
              numberOfHotels,
              categoriesIndex,
            ),
          );
        });
      }
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return series;
  }

  @computed(
    'args.containerHeight',
    'chartCategories',
    'rsSelections.filteredCompetitors.length',
    'value.name',
    'x.id',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // this is just defined by experimenting with what looks good
    // Note: in the summary view we do base the width on page width, since the modal has a min width, this is not really an issue
    // For better performance we decided to keep it static here
    const maxRectangleWidth = 200;
    const numberOfHotels = this.rsSelections.filteredCompetitors.length + 1;

    // The width of the bars, which will display the rates
    const rectangleWidth = maxRectangleWidth / numberOfHotels;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        type: 'columnrange',
        height: this.args.containerHeight,
      },
      xAxis: {
        categories: this.chartCategories,
        labels: {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
            return this.value.name;
          },
        },
      },
      yAxis: {
        title: {
          text: '',
        },
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            const label = that.hotelAndMarketService.formatRate(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.value,
              true,
              false,
            );

            return label === null ? label : `${label}`;
          },
        },
      },
      tooltip: {
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        shared: true,
        outside: false, // allows the tooltip to render outside the chart's SVG element box (required because else the chart tooltip can get cut off)
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          begin();
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          that.hoveredCompetitor = this.x.id;
          end();

          // @ts-expect-error: Incorrect type
          return document.getElementById(
            'dummy-tooltip-for-chart-roomtype-pattern-detail',
          ).innerHTML;
        },
        useHTML: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          stickyTracking: true,
          marker: {
            radius: rectangleWidth,
          },
        },
      },
    };
  }

  // Allows us to easily find the data of a hotel in "sortedSelectedRoomtypePatterns" (using the index) via the hotelinfo id
  // Note: if we ever drop IE11 support, we should be able to get the same result in 1 line by using findIndex
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelInfoToCategoriesIndexMapping() {
    const hotelInfoToCategoriesIndexMapping = {};

    this.chartCategories.forEach((hotel, index) => {
      // @ts-expect-error: Incorrect type
      hotelInfoToCategoriesIndexMapping[hotel.id] = index;
    });

    return hotelInfoToCategoriesIndexMapping;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hoveredCompetitorData() {
    const result = [];

    if (this.hoveredCompetitor) {
      // findindex is not supported in IE11 - currently fixed like this
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const hoveredPatternData =
        this.sortedSelectedRoomtypePatterns[
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          this.hotelInfoToCategoriesIndexMapping[this.hoveredCompetitor]
        ];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (!hoveredPatternData.hasNoData) {
        result.push({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          roomname: hoveredPatternData.rsbaserate.roomname,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          rate: hoveredPatternData.rate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          formattedRate: hoveredPatternData.formattedRate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          rateTypeIcons: hoveredPatternData.rsbaserate.rateTypeIcons,
        });

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        hoveredPatternData.rssubpatterns.forEach((subpattern) => {
          result.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            roomname: subpattern.rsbaserate.roomname,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            rate: subpattern.rate,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            formattedRate: subpattern.formattedRate,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            rateTypeIcons: subpattern.rsbaserate.rateTypeIcons,
          });
        });
      }
    }

    return result.sort((a, b) => b.rate - a.rate);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedSelectedRoomtypePatterns() {
    // Get the patterns of the selected roomtype (one of the roomtype_... rssubtypes)
    // @ts-expect-error: Incorrect type
    let selectedRoomtypePatterns = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.selectedRoomtypeData?.filteredPatterns.length) {
      selectedRoomtypePatterns = [
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        ...this.args.selectedRoomtypeData.filteredPatterns,
      ];
    }

    // Make sure the roomtype patterns are sorted in the same order as the chart categories (each category is a competitor)
    // @ts-expect-error: Incorrect type
    selectedRoomtypePatterns.sort((a, b) => {
      // findindex is not supported in IE11
      // const categoriesIndexA = this.chartCategories.findIndex(hotel => hotel.id === a.hotelinfo.id);
      // const categoriesIndexB = this.chartCategories.findIndex(hotel => hotel.id === b.hotelinfo.id);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const categoriesIndexA =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.hotelInfoToCategoriesIndexMapping[a.hotelinfo.id];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const categoriesIndexB =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.hotelInfoToCategoriesIndexMapping[b.hotelinfo.id];

      // regular numeric asc sort on categories indexes, to ensure same order as chart categories
      return categoriesIndexA - categoriesIndexB;
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return selectedRoomtypePatterns;
  }

  // Every series should always have the same amount of values (same amount as # of hotels/categories shown)
  // Only fill in actual values for the correct index, and fill all other indexes with dummy null value
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static fillUpSeriesDataWithDummyValues(
    // @ts-expect-error: Incorrect type
    seriesObject,
    // @ts-expect-error: Incorrect type
    pattern,
    // @ts-expect-error: Incorrect type
    numberOfHotels,
    // @ts-expect-error: Incorrect type
    hotelIndex,
  ) {
    for (let i = 0; i < numberOfHotels; i++) {
      if (i === hotelIndex) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        seriesObject.data.push(pattern.rate);
      } else {
        // Needed for correct alignment of the actual values in case some are missing
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        seriesObject.data.push(null);
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return seriesObject;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static generateSeriesConfig(pattern, color) {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      name: `${pattern.rsbaserate.roomname} (${pattern.id})`,
      type: 'scatter',
      data: [],
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      color,
      zIndex: 1,
      marker: {
        symbol: 'rectangle',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        fillColor: color,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        lineColor: color,
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    };
  }

  <template>
    <GeneralChartsOtaChart
      {{! @glint-expect-error: Incorrect type }}
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
    />

    <div class="hidden" id="dummy-tooltip-for-chart-roomtype-pattern-detail">
      <div
        class="default-tooltip default-tooltip--large default-table-tooltip rate-strategy-chart-roomtype-pattern-detail-tooltip"
      >
        <table class="default-table">
          <tbody>
            <tr>
              <td class="text-200--bold">
                {{t "rates.lowest_rate"}}
              </td>
              <td></td>
              <td class="text-200--bold">
                {{t "general.room_name"}}
              </td>
            </tr>
            {{#each this.hoveredCompetitorData as |rowData|}}
              <tr>
                <td>{{rowData.formattedRate}}</td>
                <td>
                  <div class="flex flex-row">
                    {{#each rowData.rateTypeIcons as |icon|}}
                      {{#if icon}}
                        <PrismIcon @name={{icon}} @size="extra-small" />
                      {{/if}}
                    {{/each}}
                  </div>
                </td>
                <td>{{rowData.roomname}}</td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}
